import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { signInWithApple, signInWithEmail, signUpWithEmail, isPasswordSecure } from '../lib/auth';
import { LogIn, Mail, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { FcGoogle } from 'react-icons/fc';
import supabase from "../lib/supabase";
import { setupNotificationsImmediately } from '../utils/mobileNotifications';

// Universal Link for OAuth redirects
const OAUTH_REDIRECT_URL = 'https://stridemusiccollective.co.uk/auth/callback';

// Password validation schema
const passwordSchema = z.string()
  .min(8, 'Password must be at least 8 characters')
  .refine(
    (password) => /[a-z]/.test(password),
    { message: 'Password must contain at least one lowercase letter' }
  )
  .refine(
    (password) => /[A-Z]/.test(password),
    { message: 'Password must contain at least one uppercase letter' }
  )
  .refine(
    (password) => /\d/.test(password),
    { message: 'Password must contain at least one digit' }
  )
  .refine(
    (password) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    { message: 'Password must contain at least one special character' }
  );

const schema = z.object({
  email: z.string().email('Invalid email address'),
  password: passwordSchema,
  fullName: z.string().min(2, 'Full name is required').optional(),
});

type FormData = z.infer<typeof schema>;

function Auth() {
  const { user } = useAuthStore();
  const [isSignUp, setIsSignUp] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Configuration - use Universal Link
  const redirectTo = OAUTH_REDIRECT_URL;
  const isNativelyApp = !!(window.natively || window.NativelyNotifications);

  // Defensive redirect: If user is already authenticated, redirect them away from auth page
  useEffect(() => {
    if (user) {
      console.log('🔐 [AUTH] User already authenticated, redirecting away from auth page');
      
      // Determine redirect path based on onboarding status
      let redirectPath = '/';
      
      if (!user.onboarding_completed) {
        // User needs to complete onboarding - determine which step
        if (!user.full_name || !user.display_name) {
          redirectPath = '/onboarding/name';
        } else if (!user.professions?.length) {
          redirectPath = '/onboarding/profession';
        } else if (!user.experience_level) {
          redirectPath = '/onboarding/experience';
        } else if (!user.location) {
          redirectPath = '/onboarding/location';
        } else if (!user.goals) {
          redirectPath = '/onboarding/goals';
        } else if (!user.terms_accepted) {
          redirectPath = '/onboarding/terms';
        }
      } else {
        // User has completed onboarding - check for stored redirect or go home
        const storedRedirect = sessionStorage.getItem('authRedirect');
        if (storedRedirect) {
          redirectPath = storedRedirect;
          sessionStorage.removeItem('authRedirect');
        }
      }
      
      console.log('🔐 [AUTH] Redirecting authenticated user to:', redirectPath);
      navigate(redirectPath, { replace: true });
    }
  }, [user, navigate]);

  // Clear messages when switching between sign in/up
  useEffect(() => {
    setError(null);
    setSuccessMessage(null);
  }, [isSignUp]);

  // Check for success message from password reset
  useEffect(() => {
    if (location.state?.success) {
      setSuccessMessage(location.state.success);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Initialize BuildNatively OAuth config
  useEffect(() => {
    if (window.natively?.init) {
      console.log('Initializing BuildNatively OAuth config...');
      window.natively.init({
        oauth: {
          enabled: true,
          providers: ['google', 'apple', 'spotify'],
          redirectUrl: redirectTo
        }
      });
    }
  }, [redirectTo]);

  // Diagnostic logging for BuildNatively
  useEffect(() => {
    console.log('🔍 BuildNatively diagnostics:');
    console.log('- isNativelyApp:', isNativelyApp);
    console.log('- window.natively available:', !!window.natively);
    console.log('- openExternalBrowser available:', !!window.natively?.openExternalBrowser);
    console.log('- openExternalURL available:', !!window.natively?.openExternalURL);
    if (window.natively) {
      console.log('- All natively methods:', Object.keys(window.natively));
    }
  }, []);

  // Google Sign In Handler
  const handleGoogleSignIn = async () => {
    try {
      console.log('🔵 [GOOGLE] Starting Google OAuth flow with Universal Link...');
      setIsLoading(true);
      setError(null);

      sessionStorage.setItem('authProvider', 'google');
      sessionStorage.setItem('authTimestamp', Date.now().toString());
      console.log('🔵 [GOOGLE] Session storage set, Universal Link redirectTo:', redirectTo);

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: redirectTo,
          skipBrowserRedirect: true,
        },
      });

      console.log('🔵 [GOOGLE] OAuth response:', { 
        hasUrl: !!data?.url, 
        error: error?.message,
        redirectTo: redirectTo 
      });

      if (error) {
        console.error('Google OAuth error:', error);
        setError('Failed to sign in with Google');
        return;
      }

      if (!data?.url) {
        console.error('No OAuth URL returned');
        setError('Failed to initialize Google sign in');
        return;
      }

      console.log('🔵 [GOOGLE] OAuth URL obtained, opening external browser:', data.url);

      // Handle different environments
      if (isNativelyApp) {
        console.log("🔵 [GOOGLE] ✅ Using openExternalURL with external browser (true)");
        window.natively.openExternalURL(data.url, true); // false = in-app browser
      } else {
        // Web browser
        console.log("🔵 [GOOGLE] 🌐 Web browser redirect");
        window.location.href = data.url;
      }

    } catch (err) {
      console.error('Google auth error:', err);
      setError('Failed to sign in with Google');
    } finally {
      setIsLoading(false);
    }
  };

  // Spotify Sign In Handler
  const handleSpotifySignIn = async () => {
  try {
    console.log('🟢 [SPOTIFY] Starting Spotify OAuth flow with Universal Link...');
    setIsLoading(true);
    setError(null);

    sessionStorage.setItem('authProvider', 'spotify');
    sessionStorage.setItem('authTimestamp', Date.now().toString());
    console.log('🟢 [SPOTIFY] Session storage set, Universal Link redirectTo:', redirectTo);

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'spotify',
      options: {
        redirectTo: redirectTo,
        skipBrowserRedirect: true,
        queryParams: {
          show_dialog: 'true' // Prevent caching
        }
      },
    });

    console.log('🟢 [SPOTIFY] OAuth response:', { 
      hasUrl: !!data?.url, 
      error: error?.message,
      redirectTo: redirectTo 
    });

    if (error) {
      console.error('Spotify OAuth error:', error);
      setError('Failed to sign in with Spotify');
      return;
    }

    if (!data?.url) {
      console.error('No OAuth URL returned');
      setError('Failed to initialize Spotify sign in');
      return;
    }

    console.log('🟢 [SPOTIFY] OAuth URL obtained, opening external browser:', data.url);

    // Handle different environments
    if (isNativelyApp) {
      console.log("🟢 [SPOTIFY] ✅ Using openExternalURL with external browser (true)");
      window.natively.openExternalURL(data.url, true); // true = external browser
    } else {
      // Web browser
      console.log("🟢 [SPOTIFY] 🌐 Web browser redirect");
      window.location.href = data.url;
    }

  } catch (error) {
    console.error('Spotify auth error:', error);
    setError('Failed to sign in with Spotify');
  } finally {
    setIsLoading(false);
  }
};

  // Apple Sign In Handler (uses native flow)
  const handleAppleSignIn = async () => {
  try {
    console.log('🍎 [APPLE] Starting Apple OAuth flow with Universal Link...');
    setIsLoading(true);
    setError(null);

    sessionStorage.setItem('authProvider', 'apple');
    sessionStorage.setItem('authTimestamp', Date.now().toString());
    console.log('🍎 [APPLE] Session storage set, Universal Link redirectTo:', redirectTo);

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: {
        redirectTo: redirectTo,
        skipBrowserRedirect: true,
      },
    });

    console.log('🍎 [APPLE] OAuth response:', { 
      hasUrl: !!data?.url, 
      error: error?.message,
      redirectTo: redirectTo 
    });

    if (error) {
      setError('Failed to sign in with Apple');
      return;
    }

    if (!data?.url) {
      setError('Failed to initialize Apple sign in');
      return;
    }

    console.log('🍎 [APPLE] OAuth URL obtained, opening in-app browser:', data.url);

    if (isNativelyApp) {
      console.log("🍎 [APPLE] ✅ Using openExternalURL with in-app browser (false) - complying with Apple guidelines");
      window.natively.openExternalURL(data.url, true); // false = in-app browser
    } else {
      console.log("🍎 [APPLE] 🌐 Normal browser redirect");
      window.location.href = data.url;
    }

  } catch (error: any) {
    setError('Failed to sign in with Apple');
  } finally {
    setIsLoading(false);
  }
};


  // Form setup
  const { register, handleSubmit, formState: { errors }, watch, reset } = useForm<FormData>({
    resolver: zodResolver(schema)
  });

  const password = watch('password', '');
  const passwordCheck = isPasswordSecure(password || '');

  // Email/Password form submission
  const onSubmit = async (data: FormData) => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccessMessage(null);
      
      if (isSignUp) {
        // Sign Up flow
        const metadata = {
          full_name: data.fullName,
        };

        const { data: signUpData } = await signUpWithEmail(
          data.email,
          data.password,
          metadata
        );

        if (!signUpData.user) {
          throw new Error('No user data returned from signup');
        }

        setSuccessMessage('Please check your email to verify your account before signing in.');
        reset();
      } else {
        // Sign In flow
        const { data: signInData, profile } = await signInWithEmail(
          data.email,
          data.password
        );

        if (!signInData.user) {
          throw new Error('No user data returned from signin');
        }

        // Check onboarding status
        if (!profile.onboarding_completed) {
          localStorage.setItem('onboardingStatus', 'incomplete');
          const nextStep = profile.full_name && profile.display_name ? 'profession' : 'name';
          navigate(`/onboarding/${nextStep}`, { replace: true });
        } else {
          localStorage.setItem('onboardingStatus', 'complete');
          const redirectTo = sessionStorage.getItem('authRedirect') || '/';
          sessionStorage.removeItem('authRedirect');
          navigate(redirectTo, { replace: true });
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      if (error.message.includes('Invalid login credentials')) {
        setError('Invalid email or password');
      } else if (error.message.includes('Email not confirmed')) {
        setError('Please verify your email address before signing in');
      } else {
        setError(error.message || 'An error occurred during authentication');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Logo components
  const AppleLogo = () => (
    <svg width="20" height="20" viewBox="0 0 170 170" fill="white">
      <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.197-2.12-9.973-3.17-14.34-3.17-4.58 0-9.492 1.05-14.746 3.17-5.262 2.13-9.501 3.24-12.742 3.35-4.929 0.21-9.842-1.96-14.746-6.52-3.13-2.73-7.045-7.41-11.735-14.04-5.032-7.08-9.169-15.29-12.41-24.65-3.471-10.11-5.211-19.9-5.211-29.378 0-10.857 2.346-20.221 7.045-28.068 3.693-6.303 8.606-11.275 14.755-14.925s12.793-5.51 19.948-5.629c3.915 0 9.049 1.211 15.429 3.591 6.362 2.388 10.447 3.599 12.238 3.599 1.339 0 5.877-1.416 13.57-4.239 7.275-2.618 13.415-3.702 18.445-3.275 13.63 1.1 23.87 6.473 30.68 16.153-12.19 7.386-18.22 17.731-18.1 31.002 0.11 10.337 3.86 18.939 11.23 25.769 3.34 3.17 7.07 5.62 11.22 7.36-0.9 2.61-1.85 5.11-2.86 7.51zM119.11 7.24c0 8.102-2.96 15.667-8.86 22.669-7.12 8.324-15.732 13.134-25.071 12.375-0.119-0.972-0.188-1.995-0.188-3.07 0-7.778 3.386-16.102 9.399-22.908 3.002-3.446 6.82-6.311 11.45-8.597 4.62-2.252 8.99-3.497 13.1-3.71 0.12 1.083 0.17 2.166 0.17 3.241z"/>
    </svg>
  );

  const SpotifyLogo = () => (
    <svg width="20" height="20" viewBox="0 0 168 168" fill="white">
      <path d="M83.996 0.277C37.747 0.277 0.253 37.77 0.253 84.019C0.253 130.27 37.747 167.76 83.996 167.76C130.25 167.76 167.74 130.27 167.74 84.019C167.74 37.773 130.25 0.281 83.995 0.281L83.996 0.277ZM122.4 121.057C120.9 123.517 117.68 124.297 115.22 122.787C95.558 110.777 70.806 108.057 41.656 114.717C38.847 115.357 36.047 113.597 35.407 110.787C34.764 107.977 36.517 105.177 39.333 104.537C71.233 97.249 98.596 100.387 120.67 113.877C123.13 115.387 123.91 118.597 122.4 121.057ZM132.65 98.255C130.76 101.327 126.74 102.297 123.67 100.407C101.16 86.571 66.847 82.564 40.222 90.646C36.769 91.689 33.122 89.743 32.074 86.296C31.034 82.843 32.981 79.203 36.428 78.153C66.841 68.925 104.65 73.395 130.5 89.28C133.57 91.17 134.54 95.19 132.65 98.26V98.255ZM133.53 74.511C106.54 58.48 62.01 57.006 36.241 64.827C32.103 66.082 27.727 63.746 26.473 59.608C25.219 55.468 27.553 51.095 31.694 49.837C61.275 40.857 110.45 42.592 141.524 61.039C145.254 63.248 146.474 68.055 144.264 71.772C142.064 75.494 137.244 76.721 133.534 74.511H133.53Z"/>
    </svg>
  );

  return (
    <div className="min-h-screen bg-surface-light px-4 py-8 flex flex-col">
      <div className="flex-1 flex flex-col justify-center max-w-sm mx-auto w-full">
        <div className="text-center mb-20">
          <img 
            src="https://qkvjqrwihdzuyrlcsgsm.supabase.co/storage/v1/object/public/logos/logos/logo_transparent.png"
            alt="Logo"
            className="w-64 h-64 mx-auto"
          />
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 rounded-full bg-secondary-dark bg-opacity-10 text-primary text-sm flex items-start">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-3 rounded-full bg-accent bg-opacity-10 text-primary text-sm flex items-start">
            <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{successMessage}</span>
          </div>
        )}

        {/* OAuth Sign In Buttons */}
        <div className="space-y-3 mb-4">
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading}
            className="w-full h-12 bg-white hover:bg-gray-50 text-gray-800 font-medium rounded-full transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-200"
          >
            <FcGoogle className="w-5 h-5" />
            Continue with Google
          </button>

          <button
            onClick={handleAppleSignIn}
            disabled={isLoading}
            className="w-full h-12 bg-black hover:bg-gray-900 text-white font-medium rounded-full transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <AppleLogo />
            Continue with Apple
          </button>

          <button
            onClick={handleSpotifySignIn}
            disabled={isLoading}
            className="w-full h-12 bg-[#1DB954] hover:bg-[#1ed760] text-white font-medium rounded-full transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <SpotifyLogo />
            Continue with Spotify
          </button>
        </div>
      </div>
    </div>
  );
}

export default Auth;